import {useState} from 'react'
import {useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Switch} from '@/components/ui/switch'
import {Tag} from '@/components/ui/tag'
import {PorQue} from '@/components/ui/por-que'
import type {ConfirmedBy} from '@/hooks/salud/perfil_riesgo/use-perfil'
import {formatDate} from '@/lib/risk'

/** `?desplegado=1` es el enlace imprimible (T14): lo que vive en un popover cerrado no se imprime. */
export function useTrazabilidad(): [boolean, (valor: boolean) => void] {
  const [params] = useSearchParams()
  const [desplegado, setDesplegado] = useState(params.get('desplegado') === '1')
  return [desplegado, setDesplegado]
}

/** Uno por sección, en `actions` del `SectionCard` (T14): nunca un disparador por fila. */
export function TrazabilidadSwitch({
  desplegado,
  onChange,
  testId,
}: {
  desplegado: boolean
  onChange: (valor: boolean) => void
  testId: string
}) {
  const {t} = useTranslation()
  const label = t('perfilRiesgo.common.verTrazabilidad')
  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">{label}</span>
      <Switch checked={desplegado} onCheckedChange={onChange} aria-label={label} data-testid={testId} />
    </div>
  )
}

/** El estado va en un chip y nunca plegado (U5, T15). */
export type EstadoDeDato = 'pendiente' | 'sinResponder'

const TONO_DEL_ESTADO: Record<EstadoDeDato, 'amber' | 'red'> = {
  pendiente: 'amber',
  sinResponder: 'amber',
}

export function EstadoDato({estado, testId = 'estado-dato'}: {estado: EstadoDeDato; testId?: string}) {
  const {t} = useTranslation()
  // Claves literales y no por plantilla: el gate `i18n-keys` solo ve las literales.
  const rotulo: Record<EstadoDeDato, string> = {
    pendiente: t('common.senal.pendiente'),
    sinResponder: t('common.senal.sinResponder'),
  }
  return (
    <Tag tone={TONO_DEL_ESTADO[estado]} data-testid={testId}>
      {rotulo[estado]}
    </Tag>
  )
}

/** Plegada por sección (T7) e impresa en línea al desplegarse (U10, T14). Sin traza devuelve
 * `null`: ese caso ya lo dice `EstadoDato`. */
export function Procedencia({
  confirmedBy,
  confirmedAt,
  desplegado,
}: {
  confirmedBy: ConfirmedBy | null
  confirmedAt: string | null
  desplegado: boolean
}) {
  const {t} = useTranslation()
  if (!desplegado || !confirmedBy || !confirmedAt) return null
  return (
    <PorQue
      label={t('perfilRiesgo.common.deDondeSalio')}
      titulo={t('perfilRiesgo.common.deDondeSalio')}
      contenidoTestId="provenance-detail"
      siempreVisible>
      {confirmedBy.is_system
        ? t('perfilRiesgo.common.markedDuringRegistration')
        : t('perfilRiesgo.common.markedBy', {
            name: `${confirmedBy.name} ${confirmedBy.last_name}`,
            date: formatDate(confirmedAt),
          })}
    </PorQue>
  )
}

/** Criterio único de «pendiente», compartido por hechos y peligros. */
export function sinConfirmar(confirmedBy: ConfirmedBy | null, confirmedAt: string | null) {
  return !confirmedBy || !confirmedAt
}
